
import { useEffect, useRef, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import HeroSlider from '../../components/feature/HeroSlider';
import MaterialsCarousel from '../../components/feature/MaterialsCarousel';
import AmbientesCarousel from '../../components/feature/AmbientesSection';
import CollectionsGrid from '../../components/feature/CollectionsGrid';
import WhatsAppFloating from '../../components/feature/WhatsAppFloating';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';

export default function Home() {
  useSEO({
    title: 'Navamuel - Fabricantes de Muebles de Baño a medida',
    description: 'Descubre muebles de baño de calidad premium en Toledo. Maderas naturales, diseño vanguardista, porcelánicos y materiales innovadores. Fábrica Navamuel Baños.',
    keywords: 'muebles de baño, fabricantes de muebles de baño, muebles baño toledo, muebles de baño con lavabo, muebles de baño suspendidos, muebles baño a medida, muebles baño madera, muebles baño porcelánicos, baños toledo, muebles baño modernos, muebles baño pequeños, presupuesto muebles baño'
  });

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Slider */}
      <HeroSlider />

      {/* Main H1 - SEO Critical */}
      <h1 className="sr-only">Muebles de Baño Premium en Toledo - Navamuel Baños</h1>

      {/* Text Featured Section */}
      <section className="text-featured py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-light mb-8">
              Somos un nuevo concepto de funcionalidad. Somos un nuevo{' '}
              <span>concepto<em className="font-light"> de baño.</em></span>
            </h2>
            <div className="text-lg text-gray-600 mb-8">
              <p>
                Conoce Baños Navamuel Experience, un recorrido para entender nuestra filosofía, 
                conocer nuestros principios y alcance. Treinta años de experiencia en diseño de muebles de baño de calidad premium.
              </p>
            </div>
            <Link 
              to="/nosotros"
              className="inline-block bg-[#191a19] text-white px-8 py-4 hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              Descubre Baños Navamuel
            </Link>
          </div>
        </div>
      </section>

  {/* LazyVideo component implementation - placed inside this file for simplicity */}
      

      {/* Materials Carousel - Now with prominence */}
      <MaterialsCarousel />

      {/* Materials Hero Section */}
      <section className="fullscreen-basic relative h-screen overflow-hidden">
        <Link to="/materiales" className="block h-full relative">
          <picture className="absolute inset-0">
            <img 
              src="images/proyectos/p08.jpg"
              alt="Muebles de baño con materiales premium - Porcelánicos, piedras y maderas naturales en Toledo"
              className="w-full h-full object-cover object-center"
            />
          </picture>
          
          <div className="absolute inset-0 bg-[#191a19]/30"></div>
          
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white max-w-4xl px-4">
              <h2 className="text-4xl md:text-6xl font-light mb-6">
                Materiales <em className="font-light">Premium</em>
              </h2>
              <p className="text-xl md:text-2xl font-light mb-8">
                La excelencia en cada superficie - Calidad garantizada
              </p>
            </div>
          </div>
        </Link>
      </section>

      {/* Scala Collection Fullscreen */}
      <section className="fullscreen-basic relative h-screen overflow-hidden">
        <Link to="/piedra-natural" className="block h-full relative">
          <picture className="absolute inset-0">
            <img 
              src="/images/bano-elegante-home.webp"
              alt="Muebles de baño elegantes con piedra natural - Diseño vanguardista Navamuel Toledo"
              className="w-full h-full object-cover object-top"
            />
          </picture>
        </Link>
      </section>

      {/* Ambientes Carousel */}
      <AmbientesCarousel />

      {/* Collections Grid */}
      <CollectionsGrid />

      {/* Video Section - lazy load for better performance */}
      <section className="fullscreen-video relative w-full h-screen overflow-hidden bg-[#191a19]">
        <LazyVideo 
          src="videos/introduction-hq.mp4" 
          poster="images/materiales/materiales_home.webp"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
      </section>

      {/* Second Text Featured Section */}
      <section className="text-featured py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-light mb-8">
              Somos más que una elección de diseño.{' '}
              <span>Somo<em className="font-light">s un</em></span>{' '}
              <span>estilo <em className="font-light">de vida.</em></span>
            </h2>
            <div className="text-lg text-gray-600 mb-8">
              <p>
                Descubre los mejores proyectos de interiorismo y arquitectura que han optado por Navamuel Baños. 
                Treinta años transformando espacios con muebles de baño de calidad premium en Toledo.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold mb-3">Maderas Naturales</h3>
                <p className="text-gray-600 text-sm">
                  Roble, Nogal, Ébano, Zebrano y más. Maderas premium seleccionadas para durabilidad y belleza.
                </p>
              </div>
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold mb-3">Diseño Vanguardista</h3>
                <p className="text-gray-600 text-sm">
                  Tendencias contemporáneas en muebles de baño con innovación y funcionalidad garantizada.
                </p>
              </div>
              <div className="p-6 bg-gray-50 rounded-lg">
                <h3 className="text-xl font-semibold mb-3">Materiales Innovadores</h3>
                <p className="text-gray-600 text-sm">
                  Porcelánicos, piedras compactas y resinas minerales resistentes a la humedad.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* <ProjectSlider /> */}

      <Footer />
      <WhatsAppFloating />
    </div>
  );
}

// LazyVideo: usa IntersectionObserver para cargar el vídeo sólo cuando entra en viewport.
const LazyVideo = ({ src, poster, className = '' }: { src: string; poster: string; className?: string }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isLoaded) {
            const videoElement = entry.target as HTMLVideoElement;
            videoElement.src = videoElement.dataset.src || '';
            videoElement.load();
            setIsLoaded(true);
            observer.unobserve(videoElement);
          }
        });
      },
      { rootMargin: '50px' }
    );

    observer.observe(video);

    return () => {
      if (video) observer.unobserve(video);
    };
  }, [isLoaded]);

  return (
    <video
      ref={videoRef}
      data-src={src}
      poster={poster}
      autoPlay
      loop
      muted
      playsInline
      className={`${className} ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
      style={{ transition: 'opacity 0.3s ease-in-out' }}
    />
  );
};

